import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { Button, Field, Input, Modal, Select, Toggle, useToast } from '@w7/ui'
import {
  cmApi,
  type BrowseMarket,
  type EventSearchHit,
} from '@/api/cmApi'
import { useCmMe } from '@/api/hooks/useCmMe'
import {
  canEditBookmaker,
  canEditMarketDisplayOrder,
  canEditMarketEvents,
  canEnableBookmaker,
  canEnableMarket,
} from '@/app/staffAccess'
import { authHeaders } from '@/auth/session'
import { CmPanel } from './CmPanel'
import { CatalogLegend, CatalogWorkflow, MARKET_TOGGLE_LEGEND } from './catalogGuide'
import {
  FANCY_LIMIT_TIERS,
  fancyLimitsPayload,
  showsFancyLimitsTier,
} from './fancyLimitsType'
import { isBookmakerMarket, marketVendor, mergeBrowseMarkets } from './catalogMarkets'
import {
  formFromMarket,
  hitsWithEventOnAfterMarketEnable,
  matchOddsMarketUpdatePayload,
  type MaxBetForm,
} from './marketSelectionLimits'
import {
  MARKET_KIND_FILTERS,
  filterMarketsByKind,
  isMatchOddsMarket,
  marketKindLabel,
  type MarketKindFilter,
} from './catalogMarketKind'

function numOrNull(v: string): number | null {
  if (v.trim() === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

function isSoccer(m: BrowseMarket, event: EventSearchHit): boolean {
  return m.vendorSportId === '1' || event.vendorSportId === '1'
}

const isMatchOdds = isMatchOddsMarket

function videoStatusOn(m: BrowseMarket): boolean {
  const v = (m.videoUrlStatus || 'Off').toLowerCase()
  return v === 'on' || v === 'yes' || v === 'true'
}

export function MarketSelectionPage() {
  const toast = useToast()
  const me = useCmMe()
  const caller = me.isSuccess ? me.data?.caller : undefined
  const allowEnableMarket = me.isSuccess && canEnableMarket(caller)
  const allowEditEvents = me.isSuccess && canEditMarketEvents(caller)
  const allowDisplayOrder = me.isSuccess && canEditMarketDisplayOrder(caller)
  const allowEnableBookmaker = me.isSuccess && canEnableBookmaker(caller)
  const allowEditBookmaker = me.isSuccess && canEditBookmaker(caller)
  const [params] = useSearchParams()
  const [q, setQ] = useState('')
  const [hits, setHits] = useState<EventSearchHit[]>([])
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [marketsByEvent, setMarketsByEvent] = useState<Record<string, BrowseMarket[]>>({})
  const [editMarket, setEditMarket] = useState<BrowseMarket | null>(null)
  const [form, setForm] = useState<MaxBetForm>(formFromMarket({} as BrowseMarket))
  const [marketKindFilter, setMarketKindFilter] = useState<MarketKindFilter>('all')

  const search = useMutation({
    mutationFn: (query: string) => cmApi.eventSearch(authHeaders(), query, 50),
    onSuccess: (data) => {
      setHits(data.events)
      const first = data.events[0]
      if (first) {
        setExpandedId(first.vendorEventId)
        setMarketKindFilter('all')
      } else {
        setExpandedId(null)
      }
      toast.success(`${data.events.length} event(s)`)
    },
    onError: (err: Error) => toast.error(err.message),
  })

  useEffect(() => {
    const fromUrl = params.get('q')?.trim()
    if (!fromUrl) return
    setQ(fromUrl)
    search.mutate(fromUrl)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deep-link once on mount / param change
  }, [params])

  const loadMarkets = useMutation({
    mutationFn: async (event: EventSearchHit) => {
      const vendorEventId = event.vendorEventId
      const [exchange, bookmaker] = await Promise.all([
        cmApi.browseMarkets(authHeaders(), vendorEventId),
        cmApi.browseMarkets(authHeaders(), vendorEventId, { bookmakerOnly: true }),
      ])
      return {
        vendorEventId,
        markets: mergeBrowseMarkets(exchange.markets, bookmaker.markets),
      }
    },
    onSuccess: (data, event) => {
      setMarketsByEvent((prev) => ({ ...prev, [event.vendorEventId]: data.markets }))
    },
    onError: (err: Error) => toast.error(err.message),
  })

  useEffect(() => {
    if (!expandedId) return
    const event = hits.find((h) => h.vendorEventId === expandedId)
    if (event && !marketsByEvent[expandedId]) {
      loadMarkets.mutate(event)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once per expanded event
  }, [expandedId, hits])

  const toggleEvent = useMutation({
    mutationFn: (args: { event: EventSearchHit; enabled: boolean }) =>
      cmApi.eventStatus(authHeaders(), {
        vendor: args.event.vendor || 'betfair',
        vendorEventId: args.event.vendorEventId,
        enabled: args.enabled,
      }),
    onSuccess: (_data, args) => {
      setHits((prev) =>
        prev.map((e) =>
          e.vendorEventId === args.event.vendorEventId
            ? { ...e, enabled: args.enabled }
            : e,
        ),
      )
      toast.success(args.enabled ? 'Event enabled' : 'Event disabled')
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const toggleMarket = useMutation({
    mutationFn: (args: { market: BrowseMarket; eventId: string; enabled: boolean }) =>
      cmApi.marketStatus(authHeaders(), {
        vendor: marketVendor(args.market),
        vendorMarketId: args.market.vendorMarketId,
        action: args.enabled ? 'enable' : 'disable',
      }),
    onSuccess: (_data, args) => {
      setMarketsByEvent((prev) => ({
        ...prev,
        [args.eventId]: (prev[args.eventId] ?? []).map((m) =>
          m.vendorMarketId === args.market.vendorMarketId
            ? { ...m, enabled: args.enabled, isSuspended: !args.enabled }
            : m,
        ),
      }))
      // Market-API enable/open also opens parent event (portal VISIBLE_EVENT).
      setHits((prev) =>
        hitsWithEventOnAfterMarketEnable(prev, args.eventId, args.enabled),
      )
      toast.success(args.enabled ? 'Market on' : 'Market off')
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const patchMarket = useMutation({
    mutationFn: (args: {
      market: BrowseMarket
      eventId: string
      patch: Record<string, unknown>
      label?: string
    }) =>
      cmApi.marketUpdate(authHeaders(), {
        vendor: marketVendor(args.market),
        vendorMarketId: args.market.vendorMarketId,
        ...args.patch,
      }),
    onSuccess: (_data, args) => {
      setMarketsByEvent((prev) => ({
        ...prev,
        [args.eventId]: (prev[args.eventId] ?? []).map((m) =>
          m.vendorMarketId === args.market.vendorMarketId ? { ...m, ...args.patch } : m,
        ),
      }))
      toast.success(args.label || 'Market updated')
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const saveLimits = useMutation({
    mutationFn: async () => {
      if (!editMarket) throw new Error('No market selected')
      const maxBet = numOrNull(form.maxBet)
      const maxMarket = numOrNull(form.maxMarket)
      if (maxBet == null || maxBet <= 0) throw new Error('Max bet is required')
      if (maxMarket == null || maxMarket <= 0) throw new Error('Max market is required')
      if (maxBet > maxMarket) throw new Error('Max bet must be ≤ max market')
      const matchOdds = isMatchOdds(editMarket)
      if (matchOdds) {
        const fancyBet = numOrNull(form.maxBetFancy)
        const fancyMkt = numOrNull(form.maxMarketFancy)
        if (fancyBet != null && fancyMkt != null && fancyBet > fancyMkt) {
          throw new Error('Fancy max bet must be ≤ fancy max market')
        }
      }
      const vendor = marketVendor(editMarket)
      await cmApi.marketLimits(authHeaders(), {
        vendor,
        vendorMarketId: editMarket.vendorMarketId,
        maxBet,
        maxMarket,
        maxBetFancy: matchOdds ? numOrNull(form.maxBetFancy) : undefined,
        maxMarketFancy: matchOdds ? numOrNull(form.maxMarketFancy) : undefined,
        ...fancyLimitsPayload(editMarket, form.maxLimitsType),
      })
      if (matchOdds) {
        await cmApi.marketUpdate(
          authHeaders(),
          matchOddsMarketUpdatePayload(form, vendor, editMarket.vendorMarketId),
        )
      }
      return { vendorEventId: editMarket.vendorMarketId }
    },
    onSuccess: () => {
      toast.success('Max bet saved')
      if (editMarket && expandedId) {
        const event = hits.find((h) => h.vendorEventId === expandedId)
        if (event) loadMarkets.mutate(event)
      }
      setEditMarket(null)
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const onSearch = (e: FormEvent) => {
    e.preventDefault()
    const trimmed = q.trim()
    if (!trimmed) {
      toast.error('Enter an event name or Betfair id')
      return
    }
    search.mutate(trimmed)
  }

  const onExpand = (event: EventSearchHit) => {
    if (expandedId === event.vendorEventId) {
      setExpandedId(null)
      return
    }
    setExpandedId(event.vendorEventId)
    setMarketKindFilter('all')
    if (!marketsByEvent[event.vendorEventId]) {
      loadMarkets.mutate(event)
    }
  }

  const openEdit = (market: BrowseMarket) => {
    const canEdit = isBookmakerMarket(market) ? allowEditBookmaker : allowEnableMarket
    if (!canEdit) return
    setEditMarket(market)
    setForm(formFromMarket(market))
  }

  const patchBusy = patchMarket.isPending || toggleMarket.isPending
  const marketRowLocked = (market: BrowseMarket) => {
    const canMutate = isBookmakerMarket(market) ? allowEnableBookmaker : allowEnableMarket
    return !canMutate || patchBusy
  }
  const marketEditLocked = (market: BrowseMarket) => {
    const canEdit = isBookmakerMarket(market) ? allowEditBookmaker : allowEnableMarket
    return !canEdit || patchBusy
  }

  const expandedMarkets = useMemo(() => {
    const raw = expandedId ? marketsByEvent[expandedId] ?? [] : []
    return filterMarketsByKind(raw, marketKindFilter)
  }, [expandedId, marketsByEvent, marketKindFilter])

  const expandedMarketTotal = expandedId ? (marketsByEvent[expandedId] ?? []).length : 0

  return (
    <CmPanel
      wide
      title="Market selection"
      subtitle="All market types — Match Odds, fancy, session, bookmaker — with limits, scorecard, and live TV"
    >
      <div className="cm-catalog-card">
        <CatalogWorkflow
          steps={[
            {
              title: 'Search',
              body: 'Team name or Betfair event id. Cricket shows fancy + session + Match Odds together.',
            },
            {
              title: 'Filter',
              body: 'Expand a row, then filter by market type (All, Match Odds, Fancy, Session, Bookmaker).',
            },
            {
              title: 'Edit limits',
              body: 'Click Edit for max bet, fancy limits, video URL, Betradar id, and Stats id. Saves push live to players.',
            },
          ]}
        />

        <form className="cm-msel-search" onSubmit={onSearch}>
          <Field
            label="Search event"
            hint="Event name or numeric Betfair event id. First result expands automatically."
          >
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={q}
              onChange={(ev) => setQ(ev.target.value)}
              placeholder="e.g. Sri Lanka v India or 34223314"
            />
          )}
        </Field>
        <Button type="submit" variant="primary" busy={search.isPending}>
          Search
        </Button>
      </form>

        <CatalogLegend items={MARKET_TOGGLE_LEGEND} />
      </div>

      <div className="cm-catalog-card">
      <div className="cm-msel-list" role="list">
        {!hits.length && !search.isPending ? (
          <div className="cm-ms-empty">Search for an event to begin.</div>
        ) : null}
        {hits.map((event) => {
          const open = expandedId === event.vendorEventId
          return (
            <div key={event.vendorEventId} className="cm-msel-event" role="listitem">
              <div className="cm-msel-event-row">
                <button
                  type="button"
                  className="cm-msel-expand"
                  aria-expanded={open}
                  onClick={() => onExpand(event)}
                >
                  {open ? '−' : '+'}
                </button>
                <div className="cm-msel-event-main">
                  <span className="cm-ms-name">{event.name}</span>
                  <span className="cm-ms-id">
                    Event · {event.betfairId || event.vendorEventId}
                    {event.sportName ? ` · ${event.sportName}` : ''}
                    {event.leagueName ? ` · ${event.leagueName}` : ''}
                    {event.inPlay ? ' · in-play (event)' : ''}
                  </span>
                </div>
                <label className="cm-toggle cm-msel-toggle">
                  <Toggle
                    label={event.enabled ? 'Event on' : 'Event off'}
                    checked={event.enabled}
                    disabled={!allowEditEvents || toggleEvent.isPending}
                    onChange={(v) => toggleEvent.mutate({ event, enabled: v })}
                  />
                  <span>{event.enabled ? 'On' : 'Off'}</span>
                </label>
              </div>

              {open ? (
                <div className="cm-msel-markets">
                  <div className="cm-msel-toolbar">
                    <Field label="Market type" className="cm-event-market-filter">
                      {({ id, describedBy }) => (
                        <Select
                          id={id}
                          aria-describedby={describedBy}
                          value={marketKindFilter}
                          onChange={(ev) =>
                            setMarketKindFilter(ev.target.value as MarketKindFilter)
                          }
                          options={MARKET_KIND_FILTERS.map((o) => ({
                            value: o.value,
                            label: o.label,
                          }))}
                        />
                      )}
                    </Field>
                    <span className="cm-ms-id">
                      {expandedMarketTotal
                        ? `${expandedMarkets.length} of ${expandedMarketTotal} market(s) shown`
                        : ''}
                    </span>
                  </div>
                  {loadMarkets.isPending && !expandedMarkets.length ? (
                    <div className="cm-ms-empty">Loading markets…</div>
                  ) : null}
                  {!loadMarkets.isPending && expandedMarketTotal && !expandedMarkets.length ? (
                    <div className="cm-ms-empty">No markets match this filter.</div>
                  ) : null}
                  {!loadMarkets.isPending && !expandedMarketTotal ? (
                    <div className="cm-ms-empty">No markets for this event.</div>
                  ) : null}
                  {expandedMarkets.length ? (
                    <div className="cm-msel-table-wrap" aria-hidden="true">
                      <div className="cm-msel-table-head">
                        <span>Market</span>
                        <span>Status</span>
                        <span>Order</span>
                        <span>Re-Active</span>
                        <span>In-play</span>
                        <span>Suspend</span>
                        <span>Only back</span>
                        <span>Betradar</span>
                        <span>Video</span>
                        <span>Betfair odds</span>
                        <span>Limits</span>
                      </div>
                    </div>
                  ) : null}
                  {expandedMarkets.map((market) => {
                    const matchOdds = isMatchOdds(market)
                    const soccer = isSoccer(market, event)
                    const statusLocked = marketRowLocked(market)
                    const editLocked = marketEditLocked(market)
                    const flagsLocked = isBookmakerMarket(market)
                      ? editLocked
                      : statusLocked
                    return (
                      <div
                        key={market.vendorMarketId}
                        className="cm-msel-market-row cm-msel-grid cm-msel-table-wrap"
                      >
                        <div className="cm-msel-market-main">
                          <span className="cm-ms-name">{market.name}</span>
                          <span className="cm-market-kind-chip">{marketKindLabel(market)}</span>
                          {market.startTime ? (
                            <span className="cm-ms-id">
                              {new Date(market.startTime).toLocaleString()}
                            </span>
                          ) : null}
                          <span className="cm-ms-id">
                            {isBookmakerMarket(market) ? 'Bookmaker · ' : ''}
                            {market.hasLineSession ? 'Line session' : 'Exchange'} ·{' '}
                            {market.vendorMarketId}
                          </span>
                        </div>

                        <div className="cm-msel-cell">
                          <Toggle
                            label={market.enabled ? 'Market on' : 'Market off'}
                            checked={market.enabled}
                            disabled={statusLocked}
                            onChange={(v) =>
                              toggleMarket.mutate({
                                market,
                                eventId: event.vendorEventId,
                                enabled: v,
                              })
                            }
                          />
                          <span>On/Off</span>
                        </div>

                        <div className="cm-msel-cell">
                          {soccer && market.enabled ? (
                            <Input
                              aria-label="Display order"
                              value={market.sortOrder != null ? String(market.sortOrder) : ''}
                              inputMode="numeric"
                              disabled={!allowDisplayOrder || patchBusy}
                              onChange={(ev) => {
                                const n = Number(ev.target.value)
                                setMarketsByEvent((prev) => ({
                                  ...prev,
                                  [event.vendorEventId]: (prev[event.vendorEventId] ?? []).map(
                                    (m) =>
                                      m.vendorMarketId === market.vendorMarketId
                                        ? {
                                            ...m,
                                            sortOrder: Number.isFinite(n) ? n : null,
                                          }
                                        : m,
                                  ),
                                }))
                              }}
                              onBlur={(ev) => {
                                if (!allowDisplayOrder) return
                                const n = Number(ev.target.value)
                                if (!Number.isFinite(n)) return
                                patchMarket.mutate({
                                  market,
                                  eventId: event.vendorEventId,
                                  patch: { sortOrder: n },
                                  label: 'Display order saved',
                                })
                              }}
                            />
                          ) : (
                            <span aria-hidden="true">—</span>
                          )}
                          <span>Order</span>
                        </div>

                        <div className="cm-msel-cell">
                          {matchOdds && market.enabled ? (
                            <Toggle
                              label="Re-Active"
                              checked={Boolean(market.manualActive)}
                              disabled={flagsLocked}
                              onChange={(v) =>
                                patchMarket.mutate({
                                  market,
                                  eventId: event.vendorEventId,
                                  patch: { manualActive: v },
                                  label: v ? 'Re-Active on' : 'Re-Active off',
                                })
                              }
                            />
                          ) : (
                            <span aria-hidden="true">—</span>
                          )}
                          <span>Re-Act</span>
                        </div>

                        <div className="cm-msel-cell">
                          {market.enabled ? (
                            <Toggle
                              label="In-play"
                              checked={Boolean(market.inPlay)}
                              disabled={flagsLocked}
                              onChange={(v) =>
                                patchMarket.mutate({
                                  market,
                                  eventId: event.vendorEventId,
                                  patch: { inPlay: v },
                                  label: v ? 'In-play open' : 'In-play close',
                                })
                              }
                            />
                          ) : (
                            <span aria-hidden="true">—</span>
                          )}
                          <span>In-play</span>
                        </div>

                        <div className="cm-msel-cell">
                          {market.enabled ? (
                            <Toggle
                              label="Suspend"
                              checked={Boolean(market.isSuspended)}
                              disabled={flagsLocked}
                              onChange={(v) =>
                                patchMarket.mutate({
                                  market,
                                  eventId: event.vendorEventId,
                                  patch: { isSuspended: v },
                                  label: v ? 'Suspended' : 'Unsuspended',
                                })
                              }
                            />
                          ) : (
                            <span aria-hidden="true">—</span>
                          )}
                          <span>Suspend</span>
                        </div>

                        <div className="cm-msel-cell">
                          {market.enabled ? (
                            <Toggle
                              label="Only back"
                              checked={Boolean(market.isOnlyBack)}
                              disabled={flagsLocked}
                              onChange={(v) =>
                                patchMarket.mutate({
                                  market,
                                  eventId: event.vendorEventId,
                                  patch: { isOnlyBack: v },
                                  label: 'Only back updated',
                                })
                              }
                            />
                          ) : (
                            <span aria-hidden="true">—</span>
                          )}
                          <span>Back</span>
                        </div>

                        <div className="cm-msel-cell">
                          {matchOdds && market.enabled ? (
                            <Toggle
                              label="Betradar"
                              checked={Boolean(market.isBetradar)}
                              disabled={flagsLocked}
                              onChange={(v) =>
                                patchMarket.mutate({
                                  market,
                                  eventId: event.vendorEventId,
                                  patch: { isBetradar: v },
                                  label: 'Betradar updated',
                                })
                              }
                            />
                          ) : (
                            <span aria-hidden="true">—</span>
                          )}
                          <span>Score</span>
                        </div>

                        <div className="cm-msel-cell">
                          {matchOdds && market.enabled ? (
                            <Toggle
                              label="Video status"
                              checked={videoStatusOn(market)}
                              disabled={flagsLocked}
                              onChange={(v) =>
                                patchMarket.mutate({
                                  market,
                                  eventId: event.vendorEventId,
                                  patch: { videoUrlStatus: v ? 'On' : 'Off' },
                                  label: 'Video status updated',
                                })
                              }
                            />
                          ) : (
                            <span aria-hidden="true">—</span>
                          )}
                          <span>TV</span>
                        </div>

                        <div className="cm-msel-cell">
                          {matchOdds && market.enabled ? (
                            <Toggle
                              label="Betfair odds"
                              checked={market.betfairOdds !== false}
                              disabled={flagsLocked}
                              onChange={(v) =>
                                patchMarket.mutate({
                                  market,
                                  eventId: event.vendorEventId,
                                  patch: { betfairOdds: v },
                                  label: 'Betfair odds updated',
                                })
                              }
                            />
                          ) : (
                            <span aria-hidden="true">—</span>
                          )}
                          <span>Odds</span>
                        </div>

                        <div className="cm-msel-cell">
                          {market.enabled ? (
                            <>
                              <Button
                                type="button"
                                size="sm"
                                variant="secondary"
                                disabled={editLocked}
                                onClick={() => openEdit(market)}
                              >
                                Edit
                              </Button>
                              {(market.maxBet != null || market.maxMarket != null) && (
                                <span className="cm-msel-limits-chip">
                                  {market.maxBet ?? '—'} / {market.maxMarket ?? '—'}
                                </span>
                              )}
                            </>
                          ) : (
                            <span aria-hidden="true">—</span>
                          )}
                          <span>Max bet</span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : null}
            </div>
          )
        })}
      </div>
      </div>

      <Modal
        open={!!editMarket}
        onClose={() => {
          if (saveLimits.isPending) return
          setEditMarket(null)
        }}
        title="Edit market limits"
        subtitle={
          editMarket
            ? `${editMarket.name} · Betfair market ${editMarket.vendorMarketId}`
            : undefined
        }
        closeDisabled={saveLimits.isPending}
        footer={
          <div className="cm-ms-modal-actions">
            <Button
              type="button"
              variant="primary"
              busy={saveLimits.isPending}
              onClick={() => saveLimits.mutate()}
            >
              Save
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={saveLimits.isPending}
              onClick={() => setEditMarket(null)}
            >
              Close
            </Button>
          </div>
        }
      >
        <div className="cm-form">
          <div className="cm-form-grid">
            <Field
              label="Max bet"
              required
              hint="Maximum stake per bet on this market. Must be ≤ max market."
            >
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  value={form.maxBet}
                  onChange={(ev) => setForm((f) => ({ ...f, maxBet: ev.target.value }))}
                  inputMode="decimal"
                />
              )}
            </Field>
            <Field
              label="Max market"
              required
              hint="Maximum total liability/exposure allowed on this market."
            >
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  value={form.maxMarket}
                  onChange={(ev) => setForm((f) => ({ ...f, maxMarket: ev.target.value }))}
                  inputMode="decimal"
                />
              )}
            </Field>
          </div>
          {editMarket && showsFancyLimitsTier(editMarket) ? (
            <Field
              label="Fancy limits tier"
              hint="Preset fancy/session limit band (Minor, Medium, Major, etc.)."
            >
              {({ id, describedBy }) => (
                <select
                  id={id}
                  aria-describedby={describedBy}
                  className="cm-input"
                  value={form.maxLimitsType}
                  onChange={(ev) =>
                    setForm((f) => ({ ...f, maxLimitsType: ev.target.value }))
                  }
                >
                  {FANCY_LIMIT_TIERS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          ) : null}
          {editMarket && isMatchOdds(editMarket) ? (
            <>
              <div className="cm-form-grid">
                <Field label="Max bet (fancy)" hint="Fancy/session max stake on Match Odds.">
                  {({ id, describedBy }) => (
                    <Input
                      id={id}
                      aria-describedby={describedBy}
                      value={form.maxBetFancy}
                      onChange={(ev) => setForm((f) => ({ ...f, maxBetFancy: ev.target.value }))}
                      inputMode="decimal"
                    />
                  )}
                </Field>
                <Field label="Max market (fancy)" hint="Fancy/session max market cap.">
                  {({ id, describedBy }) => (
                    <Input
                      id={id}
                      aria-describedby={describedBy}
                      value={form.maxMarketFancy}
                      onChange={(ev) =>
                        setForm((f) => ({ ...f, maxMarketFancy: ev.target.value }))
                      }
                      inputMode="decimal"
                    />
                  )}
                </Field>
                <Field
                  label="Market in-play time"
                  hint="When the market should open for in-play betting."
                >
                  {({ id, describedBy }) => (
                    <Input
                      id={id}
                      aria-describedby={describedBy}
                      type="datetime-local"
                      value={form.marketInPlayAt}
                      onChange={(ev) =>
                        setForm((f) => ({ ...f, marketInPlayAt: ev.target.value }))
                      }
                    />
                  )}
                </Field>
              </div>
              <Field
                label="Video URL"
                hint="Live stream URL shown on the player portal when Video toggle is On."
              >
                {({ id, describedBy }) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    value={form.videoUrl}
                    onChange={(ev) => setForm((f) => ({ ...f, videoUrl: ev.target.value }))}
                  />
                )}
              </Field>
              <Field
                label="Channel id (Nano cosmos)"
                hint="Alternate provider channel id when using Nano cosmos feed."
              >
                {({ id, describedBy }) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    value={form.channelId}
                    onChange={(ev) => setForm((f) => ({ ...f, channelId: ev.target.value }))}
                  />
                )}
              </Field>
              <Field
                label="Betradar id"
                hint="Sportradar match id for scorecard. Saving also enables Betradar when non-empty."
              >
                {({ id, describedBy }) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    value={form.betradarId}
                    onChange={(ev) => setForm((f) => ({ ...f, betradarId: ev.target.value }))}
                  />
                )}
              </Field>
              <Field label="Stats id" hint="Stats match id for this market.">
                {({ id, describedBy }) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    value={form.statsId}
                    onChange={(ev) => setForm((f) => ({ ...f, statsId: ev.target.value }))}
                  />
                )}
              </Field>
            </>
          ) : null}
        </div>
      </Modal>
    </CmPanel>
  )
}
